import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Loader2, CheckCircle, XCircle, Upload } from 'lucide-react'
import { Link } from 'react-router-dom'

export default function ApiTest() {
  const [openaiKey, setOpenaiKey] = useState('')
  const [nanonetsKey, setNanonetsKey] = useState('')
  const [nanonetsModelId, setNanonetsModelId] = useState('768fe153-a6f2-4b98-b2e7-14444bfa8e1d')
  const [testPrompt, setTestPrompt] = useState('Extract the business name and address from this text:\n\nAcme Corporation\n123 Main Street\nNew York, NY 10001')
  const [openaiResult, setOpenaiResult] = useState('')
  const [openaiStatus, setOpenaiStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [nanonetsResult, setNanonetsResult] = useState('')
  const [nanonetsStatus, setNanonetsStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const testOpenAI = async () => {
    if (!openaiKey) {
      setOpenaiResult('Please enter your OpenAI API key')
      setOpenaiStatus('error')
      return
    }

    setOpenaiStatus('loading')
    setOpenaiResult('')

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${openaiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: 'You are an insurance document analyzer. Extract information and return it as JSON.'
            },
            {
              role: 'user',
              content: testPrompt
            }
          ],
          temperature: 0.1,
          max_tokens: 1000
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error?.message || 'API request failed')
      }

      const content = data.choices?.[0]?.message?.content || 'No response'
      setOpenaiResult(content)
      setOpenaiStatus('success')
    } catch (error) {
      setOpenaiResult(error instanceof Error ? error.message : 'Unknown error')
      setOpenaiStatus('error')
    }
  }

  const [showRawResponse, setShowRawResponse] = useState(false)
  const [rawResponse, setRawResponse] = useState('')

  const testNanonets = async () => {
    if (!nanonetsKey) {
      setNanonetsResult('Please enter your Nanonets API key')
      setNanonetsStatus('error')
      return
    }

    if (!selectedFile) {
      setNanonetsResult('Please select a PDF file to test OCR')
      setNanonetsStatus('error')
      return
    }

    setNanonetsStatus('loading')
    setNanonetsResult('')
    setRawResponse('')

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)

      // Determine which endpoint to use
      let endpoint: string
      if (nanonetsModelId) {
        // Use specific model endpoint
        endpoint = `https://app.nanonets.com/api/v2/OCR/Model/${nanonetsModelId}/LabelFile/`
      } else {
        // Try the general pre-trained OCR model
        endpoint = 'https://app.nanonets.com/api/v2/OCR/FullText'
      }

      setNanonetsResult(`Calling: ${endpoint}...`)

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(nanonetsKey + ':')}`
        },
        body: formData
      })

      const data = await response.json()
      setRawResponse(JSON.stringify(data, null, 2))

      if (!response.ok) {
        throw new Error(data.message || data.error || `API returned ${response.status}`)
      }

      // Try multiple ways to extract text from response
      let extractedText = ''
      
      // Method 1: results[].page_data[].raw_text (Full Text OCR)
      if (data.results && Array.isArray(data.results)) {
        for (const result of data.results) {
          if (result.page_data && Array.isArray(result.page_data)) {
            for (const page of result.page_data) {
              if (page.raw_text) {
                extractedText += page.raw_text + '\n\n'
              }
              // Also try 'words' array
              if (page.words && Array.isArray(page.words)) {
                extractedText += page.words.map((w: { text: string }) => w.text).join(' ') + '\n\n'
              }
            }
          }
          // Method 2: results[].prediction (Model-based OCR)
          if (result.prediction && Array.isArray(result.prediction)) {
            for (const pred of result.prediction) {
              if (pred.ocr_text) {
                extractedText += `${pred.label}: ${pred.ocr_text}\n`
              }
            }
          }
          // Method 3: Direct text field
          if (result.text) {
            extractedText += result.text + '\n\n'
          }
        }
      }

      // Method 4: Top-level text
      if (!extractedText && data.text) {
        extractedText = data.text
      }

      // Method 5: rawText at top level
      if (!extractedText && data.rawText) {
        extractedText = data.rawText
      }

      // Method 6: message with extracted data
      if (!extractedText && data.message === 'Success' && data.result) {
        extractedText = JSON.stringify(data.result, null, 2)
      }

      if (extractedText.trim()) {
        setNanonetsResult(extractedText.trim())
        setNanonetsStatus('success')
      } else {
        setNanonetsResult('No text extracted. Check raw response below - you may need a Model ID.')
        setNanonetsStatus('success')
        setShowRawResponse(true)
      }
    } catch (error) {
      setNanonetsResult(error instanceof Error ? error.message : 'Unknown error')
      setNanonetsStatus('error')
      setShowRawResponse(true)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const StatusBadge = ({ status }: { status: 'idle' | 'loading' | 'success' | 'error' }) => {
    if (status === 'idle') return null
    if (status === 'loading') return <Badge variant="secondary"><Loader2 className="h-3 w-3 animate-spin mr-1" /> Testing...</Badge>
    if (status === 'success') return <Badge variant="default" className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" /> Success</Badge>
    return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" /> Failed</Badge>
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button asChild variant="ghost" size="icon">
            <Link to="/dashboard">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">API Test Console</h1>
            <p className="text-muted-foreground">Test your OpenAI and Nanonets API keys</p>
          </div>
        </div>

        <Tabs defaultValue="openai" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="openai">OpenAI (AI Extraction)</TabsTrigger>
            <TabsTrigger value="nanonets">Nanonets (PDF OCR)</TabsTrigger>
          </TabsList>

          <TabsContent value="openai">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Test OpenAI API
                  <StatusBadge status={openaiStatus} />
                </CardTitle>
                <CardDescription>
                  Enter your OpenAI API key and test the extraction capability
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="openai-key">OpenAI API Key</Label>
                  <Input
                    id="openai-key"
                    type="password"
                    placeholder="sk-..."
                    value={openaiKey}
                    onChange={(e) => setOpenaiKey(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Get your key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary underline">platform.openai.com</a>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="test-prompt">Test Document Text</Label>
                  <Textarea
                    id="test-prompt"
                    placeholder="Paste some document text to test extraction..."
                    value={testPrompt}
                    onChange={(e) => setTestPrompt(e.target.value)}
                    rows={5}
                  />
                </div>

                <Button onClick={testOpenAI} disabled={openaiStatus === 'loading'}>
                  {openaiStatus === 'loading' ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    'Test OpenAI'
                  )}
                </Button>

                {openaiResult && (
                  <div className="space-y-2">
                    <Label>Result</Label>
                    <pre className={`p-4 rounded-lg text-sm overflow-auto max-h-64 ${
                      openaiStatus === 'error' ? 'bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100' : 'bg-muted'
                    }`}>
                      {openaiResult}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="nanonets">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Test Nanonets OCR
                  <StatusBadge status={nanonetsStatus} />
                </CardTitle>
                <CardDescription>
                  Enter your Nanonets API key and upload a PDF to test OCR
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="nanonets-key">Nanonets API Key</Label>
                  <Input
                    id="nanonets-key"
                    type="password"
                    placeholder="Your Nanonets API key..."
                    value={nanonetsKey}
                    onChange={(e) => setNanonetsKey(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Get your key from <a href="https://app.nanonets.com/#/keys" target="_blank" rel="noopener noreferrer" className="text-primary underline">app.nanonets.com</a>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nanonets-model">Model ID (optional)</Label>
                  <Input
                    id="nanonets-model"
                    placeholder="e.g., a1b2c3d4-e5f6-7890-abcd-ef1234567890"
                    value={nanonetsModelId}
                    onChange={(e) => setNanonetsModelId(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Find your Model ID in Nanonets dashboard → Your Model → Settings. 
                    Leave empty to use general OCR.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pdf-file">PDF File</Label>
                  <div className="flex items-center gap-4">
                    <Input
                      id="pdf-file"
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="flex-1"
                    />
                    {selectedFile && (
                      <Badge variant="outline">
                        <Upload className="h-3 w-3 mr-1" />
                        {selectedFile.name}
                      </Badge>
                    )}
                  </div>
                </div>

                <Button onClick={testNanonets} disabled={nanonetsStatus === 'loading'}>
                  {nanonetsStatus === 'loading' ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing PDF...
                    </>
                  ) : (
                    'Test Nanonets OCR'
                  )}
                </Button>

                {nanonetsResult && (
                  <div className="space-y-2">
                    <Label>Extracted Text</Label>
                    <pre className={`p-4 rounded-lg text-sm overflow-auto max-h-64 whitespace-pre-wrap ${
                      nanonetsStatus === 'error' ? 'bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100' : 'bg-muted'
                    }`}>
                      {nanonetsResult}
                    </pre>
                  </div>
                )}

                {rawResponse && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>Raw API Response</Label>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setShowRawResponse(!showRawResponse)}
                      >
                        {showRawResponse ? 'Hide' : 'Show'}
                      </Button>
                    </div>
                    {showRawResponse && (
                      <pre className="p-4 rounded-lg text-xs overflow-auto max-h-96 bg-muted">
                        {rawResponse}
                      </pre>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>How it works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p><strong>1. Nanonets OCR</strong> - Extracts text from PDF documents (scanned or digital)</p>
            <p><strong>2. OpenAI</strong> - Analyzes the extracted text and identifies key insurance fields</p>
            <p className="pt-4 border-t">
              Once both APIs work here, you can set them as Supabase secrets for automatic processing:
            </p>
            <pre className="bg-muted p-3 rounded-lg text-xs">
{`supabase secrets set OPENAI_API_KEY=sk-...
supabase secrets set NANONETS_API_KEY=...`}
            </pre>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
